import throttle from "lodash.throttle";
import { useEffect, useState } from "react";
import { useRouteLoaderData } from "react-router";

import { Button, Card, Heading, Span } from "@icg360/design-system";

import { Banner } from "../../old/components/Banner/Banner";
import { Header } from "../../old/components/Header/Header";
import { Section } from "../../old/components/Section/Section";
import { AppLoaderData } from "../../types";
import { useFlags } from "../../utils/feature-flags";
import { formatValueFromBE } from "../../utils/formatting";
import { Footer } from "../footer/Footer";
import { LiftLockFooter } from "../lift-lock-footer/LiftLockFooter";
import { PaymentPlans } from "../payment-plans/PaymentPlans";
import styles from "./Proposal.module.css";

const FULL_PAY_PLAN_CARRIERS = ["lloyds", "trium", "evanston"]; // lloyds is Vave, evanston is Markel

export const Proposal = () => {
  const proposal = useRouteLoaderData<AppLoaderData>("root")!;
  const [scrollY, setScrollY] = useState(0);

  const { liftLock } = useFlags();

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    const throttledHandleScroll = throttle(handleScroll, 500);

    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, []);

  const showLiftLockFooter =
    !!liftLock &&
    proposal.quote.roofResealingStripeIntentClientSecret &&
    proposal.quote.roofResealingStripeIntentStatus ===
      "requires_payment_method";
  const { insuranceCarrier, policyPeriod, customerName, customerAddress } =
    proposal.sectionFieldsMap;

  const isFullPayOnly = FULL_PAY_PLAN_CARRIERS.some(
    carrier => !!insuranceCarrier?.toLowerCase().includes(carrier)
  );
  const paymentSections = isFullPayOnly
    ? proposal.payment.sections.filter(plan => plan.id === "full-pay-plan")
    : proposal.payment.sections;
  return (
    <>
      <div className={styles.container}>
        {!showLiftLockFooter && scrollY > 350 ? (
          <Card className={styles.fixedQuoteDS}>
            <Span>
              <Heading className={styles.styledHeadingDS} size="xl">
                {formatValueFromBE(proposal.premium)}
              </Heading>
              <Heading className={styles.styledHeadingDS} size="md">
                /yr
              </Heading>
            </Span>
            <p>
              <Span bold>{proposal.producer.name}</Span>
            </p>
            <Span className={styles.phoneText}>
              {proposal.producer.agentPhoneNumber}
            </Span>
            <div className={styles.callToActionButtons}>
              <div>
                <Button
                  appearance="secondary"
                  leadingIcon="Envelope"
                  onPress={() => {
                    window.location.href = `mailto:${proposal.producer.agentEmail}`;
                  }}
                >
                  Email me
                </Button>
              </div>

              <div className={styles.phoneButton}>
                <Button
                  leadingIcon="Phone"
                  onPress={() => {
                    window.location.href = `tel:${proposal.producer.agentPhoneNumber}`;
                  }}
                >
                  Call me
                </Button>
              </div>
            </div>
          </Card>
        ) : null}
        {policyPeriod && insuranceCarrier && (
          <Header
            quoteNumber={proposal.quoteNumber}
            policyPeriod={policyPeriod}
            insuranceCarrier={insuranceCarrier}
            insuranceCarrierUrl={proposal.carrierLink}
          />
        )}
        {customerName && (
          <Banner
            customerName={customerName}
            premium={proposal.premium}
            producerName={proposal.producer.name}
            producerAgency={proposal.producer.agency}
            producerEmail={proposal.producer.agentEmail}
            producerPhone={proposal.producer.agentPhoneNumber}
          />
        )}
        <div className={styles.mainContentWrapper}>
          <div className={styles.propertyAddress}>
            <Heading tag="h5" size="xs" className={styles.addressHeadingDS}>
              Property Address
            </Heading>
            <Span>{customerAddress}</Span>
          </div>
          <div className={styles.sectionsContainer}>
            {proposal.quote.sections.slice(1).map((sectionData, index) => (
              <div
                key={sectionData.label ?? `section-${index}`}
                className={styles.sectionWrapper}
              >
                <Section sectionData={sectionData} />
              </div>
            ))}
            <div className={styles.sectionWrapper}>
              <PaymentPlans
                payment={proposal.payment}
                paymentSections={paymentSections}
              />
            </div>
          </div>
        </div>
        <Footer carrierLogoUrl={proposal.carrierLogoUrl} />
      </div>
      {showLiftLockFooter ? (
        <LiftLockFooter
          price={proposal.quote.roofResealingPaymentAmount}
          blockType={proposal.quote.roofResealingType}
        />
      ) : null}
    </>
  );
};
